import { IAdminPatientDevice } from "../../../../@types/admin/AdminDevice";
import { useDisconnectPatientDevice } from "../../../../hooks/mutations/admin/devices";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalFormActions,
  ModalTitle,
  ModalValue,
  WarningNotice,
} from "../AdminPage.styles";

interface IDisconnectDeviceModalProps {
  device: IAdminPatientDevice;
  onClose: () => void;
}

/**
 * Confirms disconnecting a device — Retool's `requireConfirmation` on
 * `deleteRemoveDevice`.
 *
 * ⚠️ The consequence is stated rather than implied: the patient's readings
 * from this device stop. Retool's dialog only asked "are you sure?".
 */
export function DisconnectDeviceModal({
  device,
  onClose,
}: IDisconnectDeviceModalProps) {
  const { showToast } = useToast();
  const { disconnectDevice, isDisconnecting } = useDisconnectPatientDevice();

  const handleConfirm = async () => {
    try {
      await disconnectDevice(device.identifier);
      showToast(`The device ${device.identifier} was disconnected.`, "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "The device could not be disconnected."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Disconnect device</ModalTitle>

      <WarningNotice>
        {device.patientName ?? "This patient"} will stop sending readings from
        this device. Data already recorded is kept.
      </WarningNotice>

      <ModalValue>
        {device.deviceName} · {device.identifier}
      </ModalValue>

      <ModalFormActions>
        <FilterButton
          type="button"
          variant="secondary"
          disabled={isDisconnecting}
          onClick={onClose}
        >
          Cancel
        </FilterButton>
        <FilterButton
          type="button"
          variant="primary"
          disabled={isDisconnecting}
          onClick={handleConfirm}
        >
          {isDisconnecting ? "Disconnecting..." : "Disconnect"}
        </FilterButton>
      </ModalFormActions>
    </ModalContainer>
  );
}
